// Silent revival of a REVOKED signaling session. The server revoking a
// session (reuse fork, "sign out others", admin action) used to leave the
// signaling client permanently stopped — a red dot until the app was
// restarted, even though the device still holds the identity key and can
// mint a fresh session in one round trip. This watcher notices the
// 'revoked' status and drives that re-mint with exponential backoff:
// getAccessToken() forces the account service's refresh-or-key-login path,
// and on success the engine is started again (CallEngine.start resets the
// stopped client). If re-login genuinely fails — deleted account, stale
// password proof after a password change elsewhere — the backoff keeps the
// attempts rare and the dot honestly red; a device that SHOULD be locked
// out stays locked out server-side either way.

interface RevivalEngine {
  start(): void;
  state: {
    get(): { signaling: string };
    subscribe(listener: () => void): () => void;
  };
}

export interface SessionRevivalDeps {
  engine: RevivalEngine;
  /** Must force a valid session (accountService.getAccessToken re-mints). */
  getAccessToken: () => Promise<string>;
  /** Injectable for tests. */
  setTimeoutFn?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeoutFn?: (t: ReturnType<typeof setTimeout>) => void;
}

const BASE_DELAY_MS = 2_000;
const MAX_DELAY_MS = 5 * 60_000;

/** Watches the engine for 'revoked' and revives the session. Returns a disposer. */
export function installSessionRevival(deps: SessionRevivalDeps): () => void {
  const setT = deps.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms));
  const clearT = deps.clearTimeoutFn ?? ((t) => clearTimeout(t));

  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let running = false;
  let disposed = false;

  async function tryRevive(): Promise<void> {
    timer = undefined;
    running = true;
    let revived = false;
    try {
      await deps.getAccessToken();
      revived = true;
    } catch {
      // Re-login failed (offline, or genuinely locked out): fall through to
      // the backoff reschedule below.
    }
    running = false;
    if (disposed) return;
    if (revived) deps.engine.start();
    else schedule();
  }

  function schedule(): void {
    if (timer !== undefined || running || disposed) return;
    const delay = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
    attempt++;
    timer = setT(() => void tryRevive(), delay);
  }

  const unsubscribe = deps.engine.state.subscribe(() => {
    const status = deps.engine.state.get().signaling;
    if (status === 'revoked') schedule();
    else if (status === 'connected') attempt = 0; // healthy again: reset backoff
  });
  // The session may already be revoked by the time this installs.
  if (deps.engine.state.get().signaling === 'revoked') schedule();

  return () => {
    disposed = true;
    unsubscribe();
    if (timer !== undefined) clearT(timer);
  };
}
